// Scripted, offline run of The Freaks for the public demo (/freaks-demo) — no API calls, made to be
// screen-recorded for socials. Game texts that normally come from the API (mission / gear / base names)
// live here in en + pl; UI strings and toasts reuse the real game keys (localization/theFreaks).

export const THE_FREAKS_DEMO_PATH = '/freaks-demo';

const NAMES = {
  en: {
    puddle: 'Mud puddle splash',
    forest: 'Forest track run',
    nightTrail: 'Night forest trail',
    tyres: 'Mud-terrain tyres',
    winch: 'Electric winch',
    base: '4x4 Garage',
    opponent: 'MudRaider',
  },
  pl: {
    puddle: 'Kąpiel w błotnej kałuży',
    forest: 'Przejazd leśnym duktem',
    nightTrail: 'Nocny szlak przez las',
    tyres: 'Opony błotne',
    winch: 'Wyciągarka elektryczna',
    base: 'Warsztat 4x4',
    opponent: 'MudRaider',
  },
};

/** The demo freak's starting state, in the shape GET /the-freaks returns. */
export const demoInitialState = (language) => {
  const n = NAMES[language] || NAMES.en;
  return {
    username: 'freak_demo',
    context: 'wof',
    contexts: ['wof', 'fishing'],
    level: 3,
    power: 18,
    energy: 64,
    maxEnergy: 100,
    nextEnergyAt: null,
    cash: 420,
    respect: 37,
    stats: [
      { stat: 'strength', value: 4, withGear: 4, trainPrice: 40 },
      { stat: 'skill', value: 5, withGear: 5, trainPrice: 50 },
      { stat: 'endurance', value: 3, withGear: 3, trainPrice: 30 },
      { stat: 'charisma', value: 2, withGear: 2, trainPrice: 20 },
    ],
    trainEnergy: 8,
    maxMissionRepeat: 5,
    missions: [
      { key: 'puddle', name: n.puddle, tier: 1, energy: 5, unlockRespect: 0, locked: false, chance: 0.82, cash: 15, respect: 1 },
      { key: 'forest', name: n.forest, tier: 2, energy: 10, unlockRespect: 30, locked: false, chance: 0.64, cash: 45, respect: 3 },
      { key: 'nightTrail', name: n.nightTrail, tier: 3, energy: 15, unlockRespect: 120, locked: true, chance: 0.4, cash: 110, respect: 7 },
    ],
    gear: [
      { key: 'tyres', name: n.tyres, stat: 'strength', bonus: 5, price: 300, owned: false },
      { key: 'winch', name: n.winch, stat: 'skill', bonus: 8, price: 900, owned: false, locked: true, unlockLevel: 5 },
    ],
    base: {
      name: n.base, level: 1, maxLevel: 10, incomePerHour: 20, nextIncomePerHour: 60, waiting: 160, upgradePrice: 400,
      storageHours: 8,
    },
    refill: { price: 50, left: 3, energy: 30 },
    recovery: null,
    daily: { claimable: true, streak: 4, prize: { cash: 80, energy: 20, points: 0 }, freakBonus: false },
    challengeEnergy: 15,
    record: { missionsDone: 41, challengesWon: 6, challengesLost: 2 },
    opponent: { username: n.opponent, isBot: true, context: 'wof', level: 3, respect: 34 },
  };
};

const statUp = (state, stat) => ({
  ...state,
  power: state.power + 1,
  stats: state.stats.map((item) => (item.stat === stat ? { ...item, value: item.value + 1, withGear: item.withGear + 1 } : item)),
});

/**
 * The walkthrough. Each step points at the button it presses (`target`, a data-testid of the real game
 * component), then `play(state, t)` returns the next state and the toast text it shows. Steps without `play` only explain.
 */
export const DEMO_STEPS = [
  { key: 'intro', tab: 'missions' },
  {
    key: 'daily',
    tab: 'missions',
    target: 'freaks-daily-claim',
    play: (s, t) => ({
      state: {
        ...s,
        cash: s.cash + s.daily.prize.cash,
        energy: Math.min(s.maxEnergy, s.energy + s.daily.prize.energy),
        daily: { ...s.daily, claimable: false },
      },
      toast: t('theFreaks.results.dailyClaimed', { cash: s.daily.prize.cash, energy: s.daily.prize.energy }),
    }),
  },
  {
    key: 'mission',
    tab: 'missions',
    target: 'freaks-mission-forest',
    play: (s, t) => {
      const mission = s.missions[1];
      return {
        state: { ...s, cash: s.cash + mission.cash, respect: s.respect + mission.respect, energy: s.energy - mission.energy },
        toast: t('theFreaks.results.missionWon', { name: mission.name, cash: mission.cash, respect: mission.respect }),
      };
    },
  },
  {
    key: 'repeat',
    tab: 'missions',
    target: 'freaks-mission-repeat-puddle',
    play: (s, t) => {
      const mission = s.missions[0];
      const runs = 5;
      const wins = 4;
      return {
        state: { ...s, cash: s.cash + wins * mission.cash, respect: s.respect + wins * mission.respect, energy: s.energy - runs * mission.energy },
        toast: t('theFreaks.results.missionRepeat', { name: mission.name, runs, wins, cash: wins * mission.cash, respect: wins * mission.respect }),
      };
    },
  },
  {
    key: 'train',
    tab: 'train',
    target: 'freaks-train-strength',
    play: (s, t) => {
      const strength = s.stats.find((item) => item.stat === 'strength');
      return {
        state: { ...statUp(s, 'strength'), cash: s.cash - strength.trainPrice, energy: s.energy - s.trainEnergy },
        toast: t('theFreaks.results.trained', { stat: t('theFreaks.stats.strength'), value: strength.value + 1 }),
      };
    },
  },
  {
    key: 'gear',
    tab: 'gear',
    target: 'freaks-gear-tyres',
    play: (s, t) => {
      const item = s.gear[0];
      return {
        state: {
          ...s,
          cash: s.cash - item.price,
          power: s.power + item.bonus,
          gear: s.gear.map((g) => (g.key === item.key ? { ...g, owned: true } : g)),
          stats: s.stats.map((st) => (st.stat === item.stat ? { ...st, withGear: st.withGear + item.bonus } : st)),
        },
        toast: t('theFreaks.results.gearBought', { name: item.name }),
      };
    },
  },
  {
    key: 'base',
    tab: 'base',
    target: 'freaks-base-collect',
    play: (s, t) => ({
      state: { ...s, cash: s.cash + s.base.waiting, base: { ...s.base, waiting: 0 } },
      toast: t('theFreaks.results.collected', { cash: s.base.waiting }),
    }),
  },
  {
    key: 'fight',
    tab: 'fight',
    target: 'freaks-challenge-demo',
    play: (s, t) => ({
      state: {
        ...s,
        cash: s.cash + 65,
        respect: s.respect + 3,
        energy: s.energy - s.challengeEnergy,
        record: { ...s.record, challengesWon: s.record.challengesWon + 1 },
        opponent: null,
      },
      toast: t('theFreaks.results.challengeWon', { opponent: `${s.opponent.username} 🤖`, cash: 65, respect: 3 }),
    }),
  },
  { key: 'outro', tab: 'fight' },
];

/** State shown at the start of each step (index) — every step plays on top of the previous ones. */
export const demoStates = (language) => DEMO_STEPS.reduce(
  (states, step) => [...states, step.play ? step.play(states[states.length - 1], (key) => key).state : states[states.length - 1]],
  [demoInitialState(language)],
);
